import axios from "axios";
import { getApiUrlWithVersion, getPythonApiUrlWithVersion } from "./environmentService";
import { getAuthUser, getCurrentUserEmail } from "./authService";
import { OnboardingBlueprint, BlueprintSection, DetailCheckboxOption, OnboardingFormData, InstantSampleSettings } from "../components/Onboarding/Types";
import {
  COMMUNITY_CONTENT_CIO, PREPPED_SAMPLE_DATA,
  SAMPLE_EVENTS_CIO,
  SAMPLE_JOBS_CIO,
  SAMPLE_NEWS_CIO
} from "../components/Onboarding/SampleContent";

export interface LaunchChecklistItem {
  edition: {
    id: string;
    title: string;
    status: string;
    scheduled_send_start_date?: string;
    total_deliveries?: number;
    open_rate?: number;
  } | null;
  complete: boolean;
  notes: string | null;
}

export interface BrandKitFile {
  url: string;
  filename: string;
}

export interface LaunchChecklistData {
  first_name: string | null;
  target_launch_date: string | null;
  raw_info: Record<string, any>;
  brand_website_url: string | null;
  brand_kit_files: BrandKitFile[];
  strategy_document_url: string | null;
  checklist: {
    template: LaunchChecklistItem;
    first_edition: LaunchChecklistItem;
    launch_email: LaunchChecklistItem;
    email_list: {
      complete: boolean;
    };
  };
}

export class OnboardingService {
  /**
   * Generate a sample newsletter edition with the given context.
   *
   * Returns the ID of the newly created newsletter edition.
   */
  async generateSampleNewsletterEdition(context: object): Promise<string> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/onboarding/generate_sample_newsletter_edition`,
      {context},
      {headers: this.getAuthHeaders()},
    );
    console.log("Generated sample newsletter edition:", response.data);
    return response.data.newsletter_edition_id;
  }

  // write me a function to save onboarding data. I dont want it to block
  async saveOnboardingData(
    formData: OnboardingFormData,
    generateBlueprint: boolean,
    scrapeBrandAssets: boolean,
  ): Promise<string> {
    console.log("Saving onboarding data");
    const response = await axios.post(
      `${getApiUrlWithVersion()}/onboarding/save_onboarding_data`,
      {
        ...formData,
        generateBlueprint,
        scrapeBrandAssets,
      },
      {headers: this.getAuthHeaders()},
    ).then(

      (response) => {
        console.log("Saved onboarding data:", response.data);
        return response.data;
      }
    )
    return response.data;
  }

  // TODO all these functions will need to go async later & dynmically figure out data based on website + reader
  generateSampleJobs(): DetailCheckboxOption[] {
    const email = getCurrentUserEmail();

    if (email && email in PREPPED_SAMPLE_DATA) {
      return PREPPED_SAMPLE_DATA[email].jobs;
    }

    return SAMPLE_JOBS_CIO;
  }

  generateSampleEvents(): DetailCheckboxOption[] {
    const email = getCurrentUserEmail();
    console.log("In generateSampleEvents", email);

    if (email && email in PREPPED_SAMPLE_DATA) {
      return PREPPED_SAMPLE_DATA[email].events;
    }

    return SAMPLE_EVENTS_CIO;
  }

  generateSampleCommunityContent(): DetailCheckboxOption[] {
    const email = getCurrentUserEmail();

    if (email && email in PREPPED_SAMPLE_DATA) {
      return PREPPED_SAMPLE_DATA[email].communityContent;
    }

    return COMMUNITY_CONTENT_CIO;
  }

  generateSampleNews(): DetailCheckboxOption[] {
    const email = getCurrentUserEmail();

    if (email && email in PREPPED_SAMPLE_DATA) {
      return PREPPED_SAMPLE_DATA[email].news;
    }

    return SAMPLE_NEWS_CIO;
  }

  async listOnboardingBlueprints() {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/onboarding/blueprints`,
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  async getOnboardingBlueprintById(blueprintId: string) {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/onboarding/blueprints/${blueprintId}`,
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  async createOnboardingBlueprint(blueprintData?: Partial<OnboardingBlueprint>) {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/onboarding/blueprints`,
      blueprintData || {},
      { headers: this.getAuthHeaders() },
    );

    return response.data;
  }

  async listEmailTemplates(): Promise<{ id: number; title: string; description: string | null; tags: string[] }[]> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/email_templates`,
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  async updateOnboardingBlueprint(blueprintData: {
    blueprintId: string,
    title: string,
    sections: BlueprintSection[],
    audience: string,
    additionalDetails: string,
    operatorNotes: string,
    firstPersonVoice?: string,
    sourceTemplateId?: number | null,
    referenceEditionId?: string | null,
  }) {
    const { blueprintId, ...requestBody } = blueprintData;

    const response = await axios.put(
      `${getApiUrlWithVersion()}/onboarding/blueprints/${blueprintId}`,
      requestBody,
      { headers: this.getAuthHeaders() },
    );

    return response.data;
  }

  async deleteOnboardingBlueprint(blueprintId: string) {
    const response = await axios.delete(
      `${getApiUrlWithVersion()}/onboarding/blueprints/${blueprintId}`,
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  async getOnboardingInstantSample(afterTimestamp: string) {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/onboarding/instant_sample`, {
        headers: this.getAuthHeaders(),
        params: { created_after: afterTimestamp },
      },
    );

    return response.data;
  }

  async getBeefreeWelcomeEmail(userId: string, blueprintId: string) {
    const response = await axios.post(
      `${getPythonApiUrlWithVersion()}/onboarding/welcome_email`,
      { user_id: userId, blueprint_id: blueprintId },
      { headers: this.getAuthHeaders() },
    );

    return response.data;
  }

  async getBeefreeInstantSample(userId: string, blueprintId: string) {
    const response = await axios.post(
      `${getPythonApiUrlWithVersion()}/onboarding/instant_sample`,
      { user_id: userId, blueprint_id: blueprintId },
      { headers: this.getAuthHeaders() },
    );

    return response.data;
  }

  async generateOnboardingInstantSample(audienceOfOne: string, companyWebsite: string) {

    const url = new URL(companyWebsite);
    const domain = url.hostname.replace(/^www\./, '');

    const response = await axios.post(
      `${getApiUrlWithVersion()}/onboarding/instant_sample`,
      { audienceOfOne, domain },
      { headers: this.getAuthHeaders() },
    );

    return response.data;
  }

  async getOnboardingInstantSampleSettings() {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/onboarding/instant_sample_settings`,
      { headers: this.getAuthHeaders() }
    );

    return response.data;
  }

  async saveOnboardingInstantSampleSettings(settings: InstantSampleSettings) {
    const response = await axios.put(
      `${getApiUrlWithVersion()}/onboarding/instant_sample_settings`,
      { settings },
      { headers: this.getAuthHeaders() }
    );

    return response.data;
  }

  async getLaunchChecklist(): Promise<LaunchChecklistData> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/onboarding/launch_checklist`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateLaunchChecklist(targetDate: string): Promise<void> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/onboarding/launch_checklist`,
      { target_launch_date: targetDate },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async saveBrandWebsiteUrl(url: string): Promise<void> {
    await axios.patch(
      `${getApiUrlWithVersion()}/onboarding/launch_checklist`,
      { brand_website_url: url },
      { headers: this.getAuthHeaders() }
    );
  }

  async saveStrategyDocumentUrl(url: string): Promise<void> {
    await axios.patch(
      `${getApiUrlWithVersion()}/onboarding/launch_checklist`,
      { strategy_document_url: url },
      { headers: this.getAuthHeaders() }
    );
  }

  async uploadBrandFiles(files: File[]): Promise<BrandKitFile[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append("files[]", file));
    const response = await axios.post(
      `${getApiUrlWithVersion()}/onboarding/brand_kit/upload`,
      formData,
      { headers: { ...this.getAuthHeaders(), "Content-Type": "multipart/form-data" } }
    );
    return response.data.brand_kit_files;
  }

  async deleteBrandFile(url: string): Promise<BrandKitFile[]> {
    const response = await axios.delete(
      `${getApiUrlWithVersion()}/onboarding/brand_kit/file`,
      { data: { url }, headers: this.getAuthHeaders() }
    );
    return response.data.brand_kit_files;
  }

  private getAuthHeaders(): object {
    return {
      Authorization: getAuthUser()?.accessToken,
    };
  }
}

const onboardingService = new OnboardingService();
export default onboardingService;
